import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';

export type EtlTableColumnV2<T> = {
  id: string;
  header: string;
  accessor: (row: T) => string | number | null | undefined;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right';
};

type FilterConfigV2<T> = {
  id: string;
  label: string;
  options: string[];
  getValue: (row: T) => string | null | undefined;
};

type SortState = {
  columnId: string;
  direction: 'asc' | 'desc';
};

type Props<T> = {
  rows: T[];
  columns: EtlTableColumnV2<T>[];
  filters?: FilterConfigV2<T>[];
  emptyMessage?: string;
  hideRowCount?: boolean;
};

function compareValues(
  left: string | number | null | undefined,
  right: string | number | null | undefined,
): number {
  if (left === right) {
    return 0;
  }
  if (left === null || left === undefined || left === '') {
    return 1;
  }
  if (right === null || right === undefined || right === '') {
    return -1;
  }
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }
  return String(left).localeCompare(String(right));
}

export function EtlDetailTableV2<T>({
  rows,
  columns,
  filters = [],
  emptyMessage = 'No rows match the current filters.',
  hideRowCount = false,
}: Props<T>) {
  const [sort, setSort] = useState<SortState | null>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const filteredRows = useMemo(() => {
    return rows.filter((row) => filters.every((filter) => {
      const selected = filterValues[filter.id];
      if (!selected || selected === 'all') {
        return true;
      }
      return (filter.getValue(row) ?? '').toLowerCase() === selected.toLowerCase();
    }));
  }, [rows, filters, filterValues]);

  const sortedRows = useMemo(() => {
    if (!sort) {
      return filteredRows;
    }
    const column = columns.find((entry) => entry.id === sort.columnId);
    if (!column) {
      return filteredRows;
    }
    return [...filteredRows].sort((left, right) => {
      const result = compareValues(column.accessor(left), column.accessor(right));
      return sort.direction === 'asc' ? result : -result;
    });
  }, [columns, filteredRows, sort]);

  const toggleSort = (columnId: string) => {
    setSort((current) => {
      if (!current || current.columnId !== columnId) {
        return { columnId, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { columnId, direction: 'desc' };
      }
      return null;
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {filters.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {filters.map((filter) => (
            <label key={filter.id} className="flex flex-col gap-1.5">
              <span className={portalDs.filterLabel}>{filter.label}</span>
              <select
                className={cn(portalDs.select, 'min-w-[140px]')}
                value={filterValues[filter.id] ?? 'all'}
                onChange={(event) => {
                  setFilterValues((current) => ({
                    ...current,
                    [filter.id]: event.target.value,
                  }));
                }}
              >
                <option value="all">All</option>
                {filter.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      )}

      <div className={portalDs.tableContainer}>
        <Table>
          <TableHeader>
            <TableRow className={portalDs.tableHead}>
              {columns.map((column) => {
                const isActive = sort?.columnId === column.id;
                const SortIcon = !column.sortable
                  ? null
                  : !isActive
                    ? ArrowUpDown
                    : sort?.direction === 'asc'
                      ? ArrowUp
                      : ArrowDown;

                return (
                  <TableHead
                    key={column.id}
                    className={cn(
                      portalDs.tableHeadCell,
                      column.align === 'right' && 'text-right',
                    )}
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center gap-1',
                          portalDs.tableHeadCellSortable,
                          isActive && 'text-[#5E6278]',
                        )}
                        onClick={() => toggleSort(column.id)}
                      >
                        {column.header}
                        {SortIcon ? <SortIcon className="size-3.5" /> : null}
                      </button>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className={cn(portalDs.tableCell, 'py-8 text-center', portalDs.tableCellMuted)}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, index) => (
                <TableRow key={index} className={portalDs.tableBodyRow}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={cn(
                        portalDs.tableCell,
                        column.align === 'right' && cn('text-right', portalDs.tableCellMono),
                      )}
                    >
                      {column.render ? column.render(row) : column.accessor(row) ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!hideRowCount && (
        <p className={portalDs.rowCount}>
          Showing
          {' '}
          {sortedRows.length}
          {' '}
          of
          {' '}
          {rows.length}
          {' '}
          rows
        </p>
      )}
    </div>
  );
}

export function PortalDsTableLoading({ message }: { message: string }) {
  return (
    <div className={cn('flex items-center gap-2 py-8', portalDs.bodySm)} role="status">
      <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
      {message}
    </div>
  );
}
